<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('evaluationCreate') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('create') }}</li>
            </ol>
        </nav>
        <div class="row mb-4">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="mb-3 col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description" >
            </div>
        </div>
        <div class="row mb-4">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="additionalProportion" required>{{ $t('additionalProportion') }}</label>
                <input v-model="data.additionalProportion" type="number" class="form-control" id="additionalProportion" >
            </div>
            <div class="mb-3 col-md-8">
                <label class="form-label" for="increasingValue" required>{{ $t('increasingValue') }}</label>
                <input v-model="data.increasingValue" type="number" class="form-control" id="increasingValue" >
            </div>
        </div>
        <div class="row mb-4">
            <div class="col-2">
                <input class="form-check-input" v-model="data.recalculateWeight" type="checkbox" id="recalculateWeight">
                <label class="form-check-label ps-2"  for="recalculateWeight">
                    {{ $t('recalculateWeight') }}
                </label>
            </div>
            <div class="col-2">
                <input class="form-check-input" v-model="data.weightAverage" type="checkbox" id="weightAverage">
                <label class="form-check-label ps-2"  for="weightAverage">
                    {{ $t('weightAverage') }}
                </label>
            </div>
            <div class="col-2">
                <input class="form-check-input" v-model="data.multiply" type="checkbox" id="multiply">
                <label class="form-check-label ps-2"  for="multiply">
                    {{ $t('multiply') }}
                </label>
            </div>
        </div>
        <div class="mb-3">
            <h4>{{ $t('evaluationParameters') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('parameter') }}</th>
                        <th scope="col">{{ $t('weight') }}</th>
                        <th scope="col">{{ $t('useAttribute') }}</th>
                        <th scope="col">{{ $t('weightAttribute') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(evaluationParameter, i) in data.evaluationParameters" class="align-middle"  :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removeEvaluationParameter(i)"></i></th>
                            <td>
                            <select :id="'parameter_' + i" class="form-select" @change="onSelectChange($event, i)">
                                <option selected disabled>{{ $t('select') }}</option>
                                <template v-if="parameterList.length > 0" >
                                    <option v-for="(parameter, i) in parameterList" :key="i" name="parameter" v-bind:value="parameter.id">{{ parameter.name }}</option>
                                </template>
                                <template v-if="kpiList.length > 0" >
                                    <option v-for="(kpi, i) in kpiList" :key="i" name="kpi" v-bind:value="kpi.id">{{ kpi.name }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <input v-model="evaluationParameter.weight" type="number" class="form-control" :id="'weight_' + i">
                        </td>
                        <td>
                            <input v-model="evaluationParameter.useAttribute" type="checkbox" class="form-check-input" :id="'useAttribute_' + i">
                        </td>
                        <td>
                            <select :id="'weightAttribute_' + i" class="form-select" v-model="evaluationParameter.weightAttribute" :disabled="!evaluationParameter.useAttribute">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="datasetHeaders.length > 0" >
                                    <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                                </template>
                            </select>
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addEvaluationParameter"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="mb-3">
            <h4 class="mb-3">{{ $t('evaluationFilters') }} </h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('sign') }}</th>
                        <th scope="col">{{ $t('number') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(evaluationFilter, i) in data.evaluationFilters" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeEvaluationFilter(i)"></i></th>
                        <td>
                            <select :id="'name_' + i" class="form-select" v-model="evaluationFilter.name">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="datasetHeaders.length > 0" >
                                    <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <select v-model="evaluationFilter.sign" class="form-select"  :id="'sign_' + i"> 
                                <option value="=">=</option>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                            </select>
                        </td>
                        <td>
                            <input v-model="evaluationFilter.number" type="number" class="form-control" :id="'number_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block"  @click="addEvaluationFilter"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="d-grid gap-2 d-md-flex">
            <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
    </div>
</template>

<script>
import AssetTypeApi from "../../api/server/assetType";
import DatasetApi from "../../api/server/dataset";
import EvaluationApi from "../../api/server/evaluation.js";
import kpiApi from "../../api/server/kpi.js";
import ModelingApi from "../../api/server/modeling.js";
import parameterApi from "../../api/server/parameter.js";

export default {
    name: "evaluationcreate",
    watch: {
        $route: "requestData",
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;

            this.data.keyPerformanceIndicatorId = this.$route.params.keyPerformanceIndicatorId;
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);
            
            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var evaluationList = await EvaluationApi.evaluationList(this.$route.params.id);
            this.evaluationList = evaluationList.filter(a => a.keyPerformanceIndicatorId == this.$route.params.keyPerformanceIndicatorId);

            var parameterList = await parameterApi.parameterList(this.$route.params.id);
            this.parameterList = parameterList.filter(a => a.assetTypeId == assetType.id);

            var kpiList = await kpiApi.kpiList(this.$route.params.id);
            this.kpiList = kpiList.filter(a => a.assetTypeId == assetType.id);

            this.pending = false;
        },
        async onSubmit() {
            try {
                if(this.data.name.length > 0) {
                    await EvaluationApi.evaluationCreate(this.$route.params.id, this.data);
                    this.showSuccess();
                    this.$router.go(-1);
                } else {
                    this.showEmptyNameError();
                }
            } catch {
                this.showError();
            }
        },
        onSelectChange(event, index) {
            const select = event.target;
            const option = select.options[select.selectedIndex];
            const type = option.getAttribute('name');
            const value = select.value;

            const row = this.data.evaluationParameters[index];
            if (type == "parameter") {
                row.parameterId = Number(value);
                row.kpiId = null;
            } else if (type == "kpi") {
                row.parameterId = null;
                row.kpiId = Number(value);
            }
        },
        removeEvaluationFilter(rowIndex) {
            this.data.evaluationFilters.splice(rowIndex, 1);
        },
        addEvaluationFilter() {
            this.data.evaluationFilters.push({  
                name: "",
                sign: "=",
                number: 0
            })
        },
        removeEvaluationParameter(rowIndex) {
            this.data.evaluationParameters.splice(rowIndex, 1);
        },
        addEvaluationParameter() {
            this.data.evaluationParameters.push({  
                evaluationId: 0,
                parameterId: 0,
                kpiId: 0,
                weight: 0.0,
                useAttribute: false,
                weightAttribute: ""
            })
        },
    },
    notifications: {
        showSuccess: {
            title: 'success',
            message: 'evaluationCreated',
            type: 'success'
        },
        showError: {
            title: 'error',
            message: 'evaluationError',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            data: {
                id: 0,
                name: "",
                description: "",
                evaluationParameters: [],
                evaluationFilters: [],
                multiply: false,
                recalculateWeight: false,
                weightAverage: false,
                additionalProportion: 0,
                increasingValue: 0,
            },
            parameterList: [],
            evaluationList: [],
            kpiList: [],
            pending: true,
            modelingName: ""
        }
    }
};
</script>